import { createContext } from "react";
import { TransactionListItem } from "@common/schemas/transaction.schema";
import type { AccrualConfirmDisplayVariant } from "./accrualConfirmChangeCount";

interface AccrualReviewContextValue {
    getProposedAccountId: (
        transaction: TransactionListItem,
        ledgerName?: string,
    ) => string | null;
    setProposedAccountId: (
        transactionId: number,
        accountId: string,
        ledgerName?: string,
    ) => void;
    /**
     * Live snapshot of the per-transaction proposed accrual account
     * overrides the user has set. Used by the filter-driven bulk approve
     * path to forward all known overrides to the server; the server applies
     * the ones whose transactionId it ends up loading and ignores the rest.
     */
    overrides: ReadonlyMap<number, ReadonlyMap<string, string>>;
    dismissMatch: (transactionId: number) => void;
    isMatchDismissed: (transactionId: number) => boolean;
    /**
     * `withChangeCount` badges the row Confirm / Match button with the
     * number of fields it writes and sends zero-change rows to Review. Only
     * the accrual task view uses it; the standalone page keeps `plain`.
     */
    confirmDisplay: AccrualConfirmDisplayVariant;
    /**
     * Registers the action and ledger cells with `openCellAction` so the
     * task bottom bar can trigger them. Only the accrual task view turns
     * this on; the standalone page keeps its uncontrolled pickers.
     */
    hasCellActionOpeners: boolean;
    /**
     * `button` draws an editable suggested ledger as a bordered button that
     * starts at the cell's content edge. Only the accrual task view uses it;
     * the standalone page keeps the chromeless `text` trigger.
     */
    ledgerDisplay: AccrualLedgerDisplayVariant;
}

export type AccrualLedgerDisplayVariant = "text" | "button";

export const AccrualReviewCtx = createContext<AccrualReviewContextValue>({
    getProposedAccountId: () => null,
    setProposedAccountId: () => {},
    overrides: new Map(),
    dismissMatch: () => {},
    isMatchDismissed: () => false,
    confirmDisplay: "plain",
    hasCellActionOpeners: false,
    ledgerDisplay: "text",
});
